import { z } from 'zod'

import { buildDepartUrl } from '@/lib/c2c/build-depart-url'
import { CANONICAL_CURRENCY, type DisplayCurrency } from '@/lib/currency/map'
import { ClockSchema, CountSchema, DurationSchema, MoneySchema } from '@/lib/facts/values'
import type { PsearchTrip } from '@/services/wapi/types/schedule'

const MAX_DISPLAY_TRIPS = 5

/**
 * A trip's fare in `currency`, or `undefined` when it cannot be stated in that currency.
 *
 * psearch converts server-side when the request names a `displayCurrency` (wapi#542) and
 * returns the result in a flat `converted_amount` block, which we take at its word only
 * when its own `currency` says it answers this page. Conversion is per trip and not
 * guaranteed by a 200, so the native map is the fallback — usable only when the carrier
 * already prices in the page's currency.
 *
 * Anything else yields `undefined`: a fare in some third currency is a real number that
 * would render beside the page's other prices as if it belonged to them, which is the one
 * outcome the currency contract rules out. No price beats a wrong one, and it is what a
 * dollar page does for a non-dollar carrier.
 */
function selectTripPrice(trip: PsearchTrip, currency: DisplayCurrency): number | undefined {
    if (trip.converted_amount?.currency === currency) {
        // `price`, not `total`: the base fare, matching the native branch below. `total`
        // adds fees and taxes, so taking it here would raise every displayed fare — a
        // product change wearing a currency change's clothes.
        return trip.converted_amount.price
    }
    return trip.pricing?.[currency]?.price
}

/** A station's wall-clock time for an instant (Unix seconds) at its UTC offset in hours,
 *  read through UTC so the process's own zone plays no part. `undefined` when the offset is
 *  not a number: psearch's parse only warns on drift, and a clock read at any stand-in
 *  offset would state another zone's time as the station's. */
function clockAt(utcSeconds: number, offsetHours: number): string | undefined {
    if (!Number.isFinite(offsetHours)) {
        return undefined
    }
    const local = new Date((utcSeconds + offsetHours * 60 * 60) * 1000)
    const pad = (value: number) => String(value).padStart(2, '0')
    return `${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`
}

/** One upcoming trip as the section receives it, every figure minted as the value kind the
 *  card's copy states it as — on the server from psearch, and again where the island reads
 *  the schedules API's JSON. */
export const TripDataSchema = z.strictObject({
    tripId: z.string(),
    carrierCode: z.string().optional(),
    /** The departure station's wall clock. */
    departTime: ClockSchema,
    /** The arrival station's wall clock. */
    arriveTime: ClockSchema,
    departStation: z.string(),
    arriveStation: z.string(),
    duration: DurationSchema,
    /** The fare in the page's rendered currency, absent when this trip cannot be stated in
     *  it. */
    fare: MoneySchema.optional(),
    /**
     * The carrier's own canonical-currency fare, for the `Trip Selected` payload's
     * `ticket_price_usd`. Separate from `fare` because that one follows the page: once
     * the page renders pesos, the rendered number is not a dollar amount and publishing
     * it under a `_usd` name would make the analytics field lie.
     */
    priceUsd: z.number().optional(),
    carrierName: z.string(),
    carrierLogo: z.string().optional(),
    carrierRating: z.number().optional(),
    /** `features.luxury` — renders the Luxury chip. */
    isLuxury: z.boolean(),
    /** `features.next_gen` — renders the NextGen chip. Legacy shows exactly these two
     *  chips on the schedule (timeTable) variant; the transfers pill is suppressed there. */
    isNextGen: z.boolean(),
    searchUrl: z.string(),
    transfers: CountSchema,
})
export type TripData = z.infer<typeof TripDataSchema>

interface TransformParams {
    rawTrips: PsearchTrip[]
    scores: Record<string, number>
    mode: string
    locale: string
    /** The currency the page states its prices in — the RENDERED one, so these fares
     *  match the facts-sourced prices above them on the same page. */
    currency: DisplayCurrency
    originName: string
    destName: string
    date: string
    originPlaceId: string
    destPlaceId: string
    staticBaseUrl: string
}

export function transformPsearchTrips(params: TransformParams): TripData[] {
    const {
        rawTrips,
        scores,
        mode,
        locale,
        currency,
        originName,
        destName,
        date,
        originPlaceId,
        destPlaceId,
        staticBaseUrl,
    } = params

    // Filter by transport mode (e.g. only bus trips on bus pages), matching production behavior
    const filtered = rawTrips.filter((trip) => trip.vehicle_types?.includes(mode) ?? true)

    // Sort by recommended score (lower = higher priority), matching production behavior
    const sorted = [...filtered].sort((a, b) => {
        const scoreA = scores[a.trip_id] ?? Infinity
        const scoreB = scores[b.trip_id] ?? Infinity
        return scoreA - scoreB
    })

    // A trip psearch states as no value its kind takes is left out, never shown broken, and
    // the next trip takes its place.
    return sorted
        .flatMap((trip) => {
            const durationMinutes = Math.round(
                (trip.arrive_datetime_utc - trip.depart_datetime_utc) / 60,
            )

            const operatorName = trip.itinerary_info?.itinerary?.[0]?.operator_name
            const carrierCode = trip.carrier ?? ''
            const carrierName = operatorName ?? carrierCode

            const carrierLogo = carrierCode
                ? `${staticBaseUrl}carriers/icons/${carrierCode}.png`
                : undefined

            const reviewCount = trip.reviews?.review_count ?? 0
            const carrierRating = reviewCount > 4 ? trip.reviews?.overall : undefined

            const price = selectTripPrice(trip, currency)
            const minted = TripDataSchema.safeParse({
                tripId: trip.trip_id,
                carrierCode: trip.carrier,
                departTime: clockAt(trip.depart_datetime_utc, trip.depart_timezone_offset),
                arriveTime: clockAt(trip.arrive_datetime_utc, trip.arrive_timezone_offset),
                departStation: trip.depart_name ?? trip.depart_cityname ?? '',
                arriveStation: trip.arrive_name ?? trip.arrive_cityname ?? '',
                duration: durationMinutes,
                fare: price === undefined ? undefined : { amount: price, currency },
                // Resolved the same way as the rendered fare, at the canonical currency: on a
                // dollar page a non-dollar carrier's fare reaches us CONVERTED, so reading the
                // native map alone would leave the analytics amount absent on exactly the
                // trips conversion taught the card to price.
                priceUsd: selectTripPrice(trip, CANONICAL_CURRENCY),
                carrierName,
                carrierLogo,
                carrierRating,
                isLuxury: trip.features?.luxury ?? false,
                isNextGen: trip.features?.next_gen ?? false,
                searchUrl: buildDepartUrl({
                    origin: originName,
                    destination: destName,
                    date,
                    locale,
                    query: { dpid: destPlaceId, opid: originPlaceId },
                }),
                transfers: trip.transfers,
            })
            return minted.success ? [minted.data] : []
        })
        .slice(0, MAX_DISPLAY_TRIPS)
}
